import { Injectable } from '@angular/core';
import { LstUserService } from '@savvaslearning/lst-auth';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';
import {
    getAssessmentSequence_getAssessmentSequence as AssessmentData,
    getMultiStageReport_getMultiStageReport_overallReportData_placementLevelMinMaxValues as PlacementLevelMinMaxValues,
    getMultiStageReport_getMultiStageReport_studentsReportData as StudentsReportData,
    getMultiStageReport_getMultiStageReport_studentsReportData_skillDomainData as SkillDomainData,
    getStandardsMetadata_getStandardsMetadata as StandardMetadata,
    getStandardsMetadata_getStandardsMetadata_standards as Standards,
} from '../../../../schema/graphql-types';
import { AppState } from '../core.state';
import { MultiStageReportPlayerData } from '../model/multi-stage-report-player.model';
import { MultiStageAssignmentReportDataService } from './multi-stage-assignment-report.data.service';
import {
    ActionGetAssessmentSequence,
    ActionGetMultiStageReport,
    ActionGetStandardMetadata,
    ActionResetStore,
    ActionSetInputParams,
    ActionSetIsAuthenticated,
    ActionSetIsErrorOccurred,
    ActionSetIsLoading,
    ActionSetIsStandardModalVisible,
    ActionSetLanguageLocale,
    ActionSetStandardsModalPayload,
    ActionSetStudentReport,
    ActionSetUserFeatureAttributes,
    ActionSetUserRole,
    ActionResetStandardsMetaData,
} from './store/multi-stage-assignment-report.actions';
import {
    AssessmentDetails,
    ClassOverallReportData,
    ClassStudentsReportData,
    DropdownContainerWidth,
    Locales,
    PlacementLevel,
    Report,
    SkillDomainKeys,
    StandardsMetadata,
    StandardsViewerModalMfeData,
    StandardsViewerModalPlayerData,
    StandardsViewerModalStandardsData,
    UserRole,
} from './store/multi-stage-assignment-report.model';
import {
    selectAssessmentDetails,
    selectAssessmentSequence,
    selectInputParams,
    selectIsAuthenticated,
    selectIsErrorOccurred,
    selectIsLoading,
    selectIsStandardModalVisible,
    selectIsZeroState,
    selectLanguageLocale,
    selectMetricMeasureData,
    selectOverallReportData,
    selectStandardMetadata,
    selectStandardsModalPayload,
    selectStudentsReportData,
    selectUserFeatureAttributes,
    selectUserRole,
    selectStudentsNotCompletedData,
    selectMetricsClassAverage,
    selectAssessmentReportingType,
} from './store/multi-stage-assignment-report.selectors';
@Injectable({
    providedIn: 'root',
})
export class MultiStageAssignmentReportService {
    constructor(
        private readonly store$: Store<AppState>,
        private readonly lstUserService: LstUserService,
        private readonly multiStageAssignmentReportDataService: MultiStageAssignmentReportDataService,
    ) {}
    isLoading$ = this.store$.pipe(select(selectIsLoading));
    userRole$ = this.store$.pipe(select(selectUserRole));
    overallReportData$ = this.store$.pipe(select(selectOverallReportData));
    studentsReportData$ = this.store$.pipe(select(selectStudentsReportData));
    metricMeasureData$ = this.store$.pipe(select(selectMetricMeasureData));
    metricsClassAverage$ = this.store$.pipe(select(selectMetricsClassAverage));
    selectAssessmentReportingType$ = this.store$.pipe(select(selectAssessmentReportingType));
    studentsNotCompletedData$ = this.store$.pipe(select(selectStudentsNotCompletedData));
    assessmentSequence$ = this.store$.pipe(select(selectAssessmentSequence));
    selectLanguageLocale$ = this.store$.pipe(select(selectLanguageLocale));
    selectIsErrorOccurred$ = this.store$.pipe(select(selectIsErrorOccurred));
    selectAssessmentDetails$ = this.store$.pipe(select(selectAssessmentDetails));
    selectIsZeroState$ = this.store$.pipe(select(selectIsZeroState));
    selectStandardMetadata$ = this.store$.pipe(select(selectStandardMetadata));
    isAuthenticated$ = this.store$.pipe(select(selectIsAuthenticated));
    standardsModalPayload$ = this.store$.pipe(select(selectStandardsModalPayload));
    isStandardModalVisible$ = this.store$.pipe(select(selectIsStandardModalVisible));
    selectInputParams$ = this.store$.pipe(select(selectInputParams));
    selectStudentsReportData$ = this.store$.pipe(select(selectStudentsReportData));

    setIsAuthenticated(isAuthenticated: boolean): void {
        this.store$.dispatch(ActionSetIsAuthenticated({ isAuthenticated }));
    }

    setIsLoading(isLoading: boolean): void {
        this.store$.dispatch(ActionSetIsLoading({ isLoading }));
    }

    setUserRole(userRole: UserRole): void {
        this.store$.dispatch(ActionSetUserRole({ userRole }));
    }

    setLanguageLocale(locale: Locales): void {
        this.store$.dispatch(ActionSetLanguageLocale({ locale }));
    }

    setIsErrorOccurred(isErrorOccurred: boolean): void {
        this.store$.dispatch(ActionSetIsErrorOccurred({ isErrorOccurred }));
    }

    setModalPayload(modalPayload: StandardsViewerModalMfeData): void {
        this.store$.dispatch(ActionSetStandardsModalPayload({ standardsModalPayload: modalPayload }));
    }

    setIsStandardModalVisible(isStandardModalVisible: boolean): void {
        this.store$.dispatch(ActionSetIsStandardModalVisible({ isStandardModalVisible }));
    }

    setUserRoleFromAuth(): void {
        const userRole = this.lstUserService.getRole();
        this.setUserRole(userRole as UserRole);
    }

    setInputParams(inputParams: MultiStageReportPlayerData): void {
        this.store$.dispatch(ActionSetInputParams({ inputParams }));
    }

    setMSDAReport(report: Report): void {
        this.store$.dispatch(
            ActionSetStudentReport({
                report: {
                    assessmentDetails: report.assessmentDetails,
                    overallReportData: report.overallReportData,
                    studentsReportData: report.studentsReportData,
                    framework: report.framework,
                    percentileScoreMapping: report.percentileScoreMapping,
                    studentsNotCompleted: report.studentsNotCompleted,
                    metricsClassAverage: report.metricsClassAverage,
                    assessmentReportingType: report.assessmentReportingType,
                },
            }),
        );
    }

    getReport(): void {
        this.store$.dispatch(ActionGetMultiStageReport());
    }

    resetStore(): void {
        this.store$.dispatch(ActionResetStore());
    }

    resetStandardsMetaData(): void {
        this.store$.dispatch(ActionResetStandardsMetaData());
    }

    getAssessmentSequence(assessmentSessionIds: string[]): void {
        this.store$.dispatch(ActionGetAssessmentSequence({ assessmentSessionIds }));
    }

    getStandardsMetadata(skillCodes: string[]): void {
        this.store$.dispatch(ActionGetStandardMetadata({ skillCodes }));
    }

    getMultiStageReportDataByStudentId(id?: string): Observable<ClassStudentsReportData> {
        return combineLatest([this.studentsReportData$, this.store$.pipe(select(selectInputParams))]).pipe(
            filter(([studentsReportData, inputParams]) => !!inputParams && !!studentsReportData),
            first(),
            map(([studentsReportData, inputParams]) => {
                const { studentId } = inputParams;
                if (id) {
                    return studentsReportData.find((studentReportData) => studentReportData.studentId === id);
                }
                if (studentId) {
                    const isStudentPresent = studentsReportData.find(
                        (studentReportData) => studentReportData.studentId === studentId,
                    );
                    return isStudentPresent ? isStudentPresent : studentsReportData[0];
                } else {
                    return studentsReportData[0];
                }
            }),
        );
    }

    setStudentPlacementLevelBreakdown(
        placementLevel: string,
        overallReportData: ClassOverallReportData,
        studentReportData: StudentsReportData,
    ): void {
        const studentPlacementLevelBreakdown = overallReportData.studentPlacementLevelBreakdown;
        const fullName = `${studentReportData.lastName}, ${studentReportData.firstName}`;
        switch (placementLevel) {
            case PlacementLevel.APP:
                studentPlacementLevelBreakdown.approachingExpectations.push(fullName);
                break;
            case PlacementLevel.EXC:
                studentPlacementLevelBreakdown.exceedExpectations.push(fullName);
                break;
            case PlacementLevel.DNM:
                studentPlacementLevelBreakdown.doesNotMeetsExpectations.push(fullName);
                break;
            case PlacementLevel.MET:
                studentPlacementLevelBreakdown.meetsExpectations.push(fullName);
                break;
        }
    }

    getUserFeatureValueFromStore(featureKey: string): Observable<string> {
        return combineLatest([this.store$.pipe(select(selectUserFeatureAttributes))]).pipe(
            first(),
            switchMap(([userFeatureAttributes]) => {
                if (userFeatureAttributes && userFeatureAttributes.length) {
                    const featureValue = userFeatureAttributes.find(
                        (featureAttribute) => featureAttribute.featureKey === featureKey,
                    ).featureSetting;
                    return of(featureValue);
                } else {
                    return this.multiStageAssignmentReportDataService.getUserFeatureAttributes().pipe(
                        map((data) => {
                            const featureAttribute = data.getUserFeatureAttributes;
                            this.store$.dispatch(
                                ActionSetUserFeatureAttributes({ userFeatureAttributes: featureAttribute }),
                            );
                            const featureValue = featureAttribute.find(
                                (attributes) => attributes.featureKey === featureKey,
                            ).featureSetting;
                            return featureValue;
                        }),
                    );
                }
            }),
        );
    }

    addOverallAndDomainStudentScoresToReport(
        report: Report,
        domainsScoreSum: number[],
        domainKeys: string[],
        translateValue: string[],
    ): void {
        report.studentsReportData.forEach((studentReportData) => {
            studentReportData.fullName = (studentReportData.lastName + studentReportData.firstName).toLowerCase();
            studentReportData.skillDomainData.forEach((skillDomainData, index) => {
                domainsScoreSum[index] = domainsScoreSum[index] || 0;
                domainsScoreSum[index] += skillDomainData.scaleScore;
                if (skillDomainData.skillDomainKey === SkillDomainKeys.OV) {
                    this.setOverallPlacementLevelDataForStudent(report, skillDomainData, studentReportData);
                }
                const translatedKey = translateValue[skillDomainData.skillDomainKey];
                skillDomainData.skillDomainKey =
                    translatedKey !== undefined ? translatedKey : skillDomainData.skillDomainKey;
                domainKeys[index] = skillDomainData.skillDomainKey;
            });
        });
        this.sortStudentsReportData(report);
    }

    sortStudentsReportData(report: Report): void {
        report.studentsReportData.sort((currentStudent, nextStudent) => {
            return currentStudent.fullName < nextStudent.fullName ? -1 : 1;
        });
    }

    setOverallPlacementLevelDataForStudent(
        report: Report,
        skillDomainData: SkillDomainData,
        studentReportData: ClassStudentsReportData,
    ): void {
        let overallScaledScore = 0;
        let nationalPercentile = 0;
        let placementLevel = '';
        overallScaledScore = skillDomainData.scaleScore;
        nationalPercentile = skillDomainData.nationalPercentile;
        placementLevel = skillDomainData.placementLevel;
        this.setStudentPlacementLevelBreakdown(placementLevel, report.overallReportData, studentReportData);
        studentReportData.nationalPercentile = nationalPercentile;
        studentReportData.overallScaledScore = overallScaledScore;
        studentReportData.placementLevel = placementLevel;
    }

    addClassPerformanceByDomainDataToReport(report: Report, domainsScoreSum: number[], domainKeys: string[]): void {
        let domainsScoreSumAverage = [];
        const classPerformanceByDomainData = [];
        domainsScoreSumAverage = domainsScoreSum.map((domainScoreSum, index) => {
            let numberOfStudents = 0;
            report.studentsReportData.forEach((student) => {
                if (student.skillDomainData[index].scaleScore) {
                    numberOfStudents += 1;
                }
            });
            return Math.round(domainScoreSum / numberOfStudents);
        });
        domainKeys.forEach((domainKey, index) => {
            classPerformanceByDomainData.push({
                skillDomainKey: domainKey,
                skillDomainScore: domainsScoreSumAverage[index],
            });
        });
        report.overallReportData.classPerformanceByDomainData = classPerformanceByDomainData;
    }

    filterOverallSkillDomainKeyFromReport(report: Report, translateValue: string[]): void {
        report.studentsReportData.forEach((studentReportData) => {
            studentReportData.skillDomainData = studentReportData.skillDomainData.filter((skillDomainData) => {
                return skillDomainData.skillDomainKey !== translateValue[SkillDomainKeys.OV];
            });
        });
    }
    getMinMaxValueFromPlacementValue(): PlacementLevelMinMaxValues[] {
        let meetsExpectationsMinMaxValue: PlacementLevelMinMaxValues,
            approachingExpectationsMinMaxValue: PlacementLevelMinMaxValues;
        this.overallReportData$.pipe(first()).subscribe((overallReportData) => {
            overallReportData?.placementLevelMinMaxValues?.forEach((placementLevelMinMaxValues) => {
                switch (placementLevelMinMaxValues.placementLevelCode) {
                    case PlacementLevel.MET:
                        meetsExpectationsMinMaxValue = placementLevelMinMaxValues;
                        break;
                    case PlacementLevel.APP:
                        approachingExpectationsMinMaxValue = placementLevelMinMaxValues;
                        break;
                }
            });
        });
        return [meetsExpectationsMinMaxValue, approachingExpectationsMinMaxValue];
    }

    getOverallReportDataFromStore(): Observable<ClassOverallReportData> {
        return combineLatest([this.overallReportData$]).pipe(
            filter(([overallReportData]) => !!overallReportData),
            first(),
            map(([overallReportData]) => overallReportData),
        );
    }

    getStudentsReportDataFromStore(): Observable<ClassStudentsReportData[]> {
        return combineLatest([this.studentsReportData$]).pipe(
            filter(([studentsReportData]) => !!studentsReportData),
            first(),
            map(([studentsReportData]) => studentsReportData),
        );
    }

    getAssessmentSequenceFromStore(assessmentSessionIds: string[]): Observable<AssessmentData[]> {
        return combineLatest([this.store$.pipe(select(selectAssessmentSequence))]).pipe(
            filter(
                ([assessmentSequence]) =>
                    this.filterAssessmentForSessionId(assessmentSessionIds, assessmentSequence).length > 0,
            ),
            first(),
            map(([assessmentSequence]) => assessmentSequence),
        );
    }

    sortDomainKeysInAscending(previous, next): number {
        if (previous.skillDomainKey < next.skillDomainKey) {
            return -1;
        }
        if (previous.skillDomainKey > next.skillDomainKey) {
            return 1;
        }
        return 0;
    }

    getDropdownContainerWidth(): number {
        let dropdownWidth = DropdownContainerWidth.Small;
        const offsetCharacterSize = 2;
        const minDropdownCharacterLimit = 29;
        this.studentsReportData$.pipe(first()).subscribe((studentsReportData) => {
            studentsReportData.forEach((studentReportData) => {
                const fullNameLength =
                    studentReportData.firstName.length + studentReportData.lastName.length + offsetCharacterSize;
                if (fullNameLength > minDropdownCharacterLimit) {
                    dropdownWidth = DropdownContainerWidth.Large;
                }
            });
        });
        return dropdownWidth;
    }

    getViewResourceLinkFromTermFullPath(standardFullPath: string): string {
        standardFullPath = standardFullPath.replace(/\\/g, '!5C').replace(/\s/g, '!20').replace(/\//g, '!2F');
        return `search/standard/${standardFullPath}`;
    }

    getAssessmentDetailsFromStore(): Observable<AssessmentDetails> {
        return combineLatest([this.selectAssessmentDetails$]).pipe(
            filter(([assessmentDetails]) => !!assessmentDetails),
            first(),
            map(([assessmentDetails]) => assessmentDetails),
        );
    }

    getStandardsMetadataFromStore(skillCodes: string[]): Observable<StandardsMetadata> {
        return combineLatest([this.selectStandardMetadata$]).pipe(
            filter(
                ([standardsMetadata]) =>
                    this.filterStandardsForSkillCodes(skillCodes, standardsMetadata).length === skillCodes.length,
            ),
            first(),
            map(([standardsMetadata]) =>
                this.filterStandardsForSkillCodes(skillCodes, standardsMetadata).reduce((obj, item) => {
                    return {
                        ...obj,
                        [item.skillCode]: item,
                    };
                }, {}),
            ),
        );
    }

    convertStandardsArrayToString(standardsMetaData: StandardMetadata, translateValue: string): string {
        return standardsMetaData.standards.length > 2
            ? `${standardsMetaData.standards[0].standardCode} + ${
                  standardsMetaData.standards.length - 1
              } ${translateValue}`
            : standardsMetaData.standards.map((standard) => standard.standardCode).join(', ');
    }

    filterStandardsForSkillCodes(skillCodes: string[], standardsMetadata: StandardMetadata[]): StandardMetadata[] {
        return standardsMetadata.filter((standardMetadata) => skillCodes.includes(standardMetadata.skillCode));
    }

    filterAssessmentForSessionId(
        assessmentSessionIds: string[],
        assessmentSequences: AssessmentData[],
    ): AssessmentData[] {
        return assessmentSequences.filter((assessmentSequence) =>
            assessmentSessionIds.includes(assessmentSequence.assessmentSessionId),
        );
    }

    frameAndSetModalPayloadData(
        standards: Standards[],
        title: string,
        externalContentButtonText: string,
        focusElementId: string,
    ): void {
        const standardsList: StandardsViewerModalStandardsData[] = standards.map((standardsInfo) => {
            return {
                attributes: {
                    spanishDescription: standardsInfo.standardDescription,
                    description: standardsInfo.standardDescription,
                },
                id: standardsInfo.standardFullPath,
                name: standardsInfo.standardCode,
                standardGuid: standardsInfo.standardGuid,
            };
        });
        let playerDataList: StandardsViewerModalPlayerData;
        this.selectLanguageLocale$.subscribe((languageLocale) => {
            playerDataList = {
                locale: languageLocale || Locales.EN,
                isMobile: window.innerWidth < 576,
                skillIds: [],
                title,
                isMasteryMFE: false,
                externalContentButtonText,
                isStandardAssociatedCTA: true,
            };
        });
        const modalPayloadData = {
            playerDataList,
            isStandardList: true,
            standardsList,
            focusElementId: focusElementId,
        } as StandardsViewerModalMfeData;

        this.setModalPayload(modalPayloadData);
    }

    get isOpenedInIFrame(): boolean {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    }

    get APP_BASE_ROUTE(): Observable<string> {
        return combineLatest([this.store$.pipe(select(selectInputParams))]).pipe(
            filter(([inputParams]) => !!inputParams),
            map(([inputParams]) => {
                const { classId, assignmentId, itemId, itemVersion } = inputParams;
                return `/classes/${classId}/assignments/${assignmentId}/item/${itemId}/${itemVersion}`;
            }),
        );
    }
}
